import { Box, Stack, Typography } from '@mui/material';
import { useCallback, useMemo } from 'react';
import { useNotify, useRefresh } from 'react-admin';
import { useQuery } from '@tanstack/react-query';
import { DialogCloseButton } from '@app/misc/DialogCloseButton';
import { getTask, type TaskId } from '@wwf/sdk';
import { TaskDrawerForm } from './TaskDrawerForm';
import { DrawerState } from '@app/misc/DrawerContext';
import { useDrawer } from '@app/misc/DrawerContext';

export function useTaskDrawer() {
    const drawer = useDrawer();
    const openDrawer = useCallback(
        (
            mode: 'create' | 'edit',
            selectedId: string | number | null,
            initialAssignedMemberId?: string | null
        ) =>
            drawer.openDrawer(state => (
                <TaskDrawer
                    {...state}
                    mode={mode}
                    selectedId={selectedId}
                    initialAssignedMemberId={initialAssignedMemberId}
                />
            )),
        [drawer]
    );
    const closeDrawer = useCallback(() => {
        drawer.closeDrawer();
    }, [drawer]);
    return { openDrawer, closeDrawer };
}

export function TaskDrawer({
    open,
    onClose,
    mode,
    selectedId,
    initialAssignedMemberId,
}: DrawerState & {
    mode: 'create' | 'edit';
    selectedId: string | number | null;
    initialAssignedMemberId?: string | null;
}) {
    const notify = useNotify();
    const refresh = useRefresh();

    const title = useMemo(() => {
        if (mode === 'create') return 'Create task';
        return 'Edit task';
    }, [mode]);

    const { data: selectedRecord, isPending: isPendingSelectedRecord } =
        useQuery({
            queryKey: ['task', selectedId],
            queryFn: () => getTask(selectedId as TaskId),
            enabled: open && mode === 'edit' && selectedId != null,
        });

    return (
        <Box sx={{ position: 'relative', height: '100%' }}>
            <DialogCloseButton onClose={onClose} />
            <Box p={3} pt={4}>
                <Stack direction="row" alignItems="center" mb={2} gap={1}>
                    <Typography variant="h6">{title}</Typography>
                </Stack>

                <TaskDrawerForm
                    mode={mode}
                    record={mode === 'edit' ? selectedRecord : undefined}
                    loading={mode === 'edit' && isPendingSelectedRecord}
                    initialAssignedMemberId={
                        mode === 'create' ? initialAssignedMemberId : undefined
                    }
                    onClose={onClose}
                    onSuccess={() => {
                        refresh();
                        notify(mode === 'create' ? 'Task added' : 'Saved', {
                            type: 'info',
                        });
                        onClose();
                    }}
                />
            </Box>
        </Box>
    );
}
